// src/hooks/useClientSideWorkHoursSync.js
// ========================================

import { useEffect, useRef, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { syncRapportiniAfterWorkHoursChange } from '../services/clientSideRapportiniSync';

/**
 * Hook per ascoltare i cambiamenti in workHours e sincronizzare i rapportini
 * VERSIONE CLIENT-SIDE - NESSUN COSTO AGGIUNTIVO
 */
export const useClientSideWorkHoursSync = (activeUsers = []) => {
  const listenersRef = useRef(new Map());
  const lastSyncRef = useRef(new Map());
  
  // Debounce per evitare troppe sincronizzazioni
  const debouncedSync = useCallback(
    debounce(async (userId, entries) => {
      console.log(`🔄 [CLIENT] Debounced sync per user ${userId}`);
      
      for (const entry of entries) {
        if (entry.date) {
          try {
            const result = await syncRapportiniAfterWorkHoursChange(userId, entry.date);
            if (result.updatedCount > 0) {
              console.log(`✅ [CLIENT] Sincronizzati ${result.updatedCount} rapportini per ${entry.date}`);
            }
          } catch (error) {
            console.error(`❌ [CLIENT] Errore sincronizzazione ${entry.date}:`, error);
          }
        }
      }
    }, 2000), // Aspetta 2 secondi prima di sincronizzare
    []
  );
  
  useEffect(() => {
    if (!activeUsers || activeUsers.length === 0) return;
    
    console.log(`🔄 [CLIENT] Attivazione listeners per ${activeUsers.length} utenti`);
    
    // Per ogni utente attivo, crea listener per tutti i mesi dell'anno corrente
    activeUsers.forEach(userId => {
      const currentYear = new Date().getFullYear();
      
      // Monitora gli ultimi 3 mesi e i prossimi 3 mesi
      for (let monthOffset = -3; monthOffset <= 3; monthOffset++) {
        const targetDate = new Date();
        targetDate.setMonth(targetDate.getMonth() + monthOffset);
        
        const month = (targetDate.getMonth() + 1).toString();
        const year = targetDate.getFullYear().toString();
        
        const normalizedMonth = month.replace(/^0+/, '') || month;
        const docId = `${userId}_${normalizedMonth}_${year}`;
        
        // Evita duplicati
        if (listenersRef.current.has(docId)) continue;
        
        console.log(`🔄 [CLIENT] Attivazione listener per ${docId}`);
        
        const unsubscribe = onSnapshot(
          doc(db, 'workHours', docId),
          (doc) => {
            if (doc.exists()) {
              const data = doc.data();
              const entries = data.entries || [];
              
              // Controlla se i dati sono cambiati rispetto all'ultima sincronizzazione
              const lastSyncKey = `${docId}_${JSON.stringify(entries)}`;
              const previousSyncKey = lastSyncRef.current.get(docId);
              
              if (lastSyncKey !== previousSyncKey) {
                console.log(`📊 [CLIENT] Cambiamento rilevato in ${docId}:`, entries.length, 'entries');
                
                // Aggiorna il tracking dell'ultima sincronizzazione
                lastSyncRef.current.set(docId, lastSyncKey);
                
                // Sincronizza con debounce
                debouncedSync(userId, entries);
              }
            }
          },
          (error) => {
            console.error(`❌ [CLIENT] Errore listener ${docId}:`, error);
          }
        );
        
        // Salva il listener
        listenersRef.current.set(docId, unsubscribe);
      }
    });
    
    // Cleanup quando cambiano gli utenti attivi
    return () => {
      console.log(`🔄 [CLIENT] Cleanup listeners`);
      listenersRef.current.forEach((unsubscribe, docId) => {
        if (!activeUsers.some(userId => docId.startsWith(userId))) {
          unsubscribe();
          listenersRef.current.delete(docId);
          lastSyncRef.current.delete(docId);
        }
      });
    };
    
  }, [activeUsers, debouncedSync]);
  
  // Cleanup generale
  useEffect(() => {
    return () => {
      listenersRef.current.forEach((unsubscribe) => {
        unsubscribe();
      });
      listenersRef.current.clear();
      lastSyncRef.current.clear();
    };
  }, []);
  
  // Funzione per sincronizzazione manuale
  const forceSyncAll = useCallback(async () => {
    console.log(`🔄 [CLIENT] Sincronizzazione manuale forzata`);
    
    for (const userId of activeUsers) {
      try {
        // Sincronizza gli ultimi 7 giorni per ogni utente
        const promises = [];
        for (let i = 0; i < 7; i++) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          
          promises.push(syncRapportiniAfterWorkHoursChange(userId, dateStr));
        }
        
        await Promise.allSettled(promises);
        
      } catch (error) {
        console.error(`❌ [CLIENT] Errore sync manuale per ${userId}:`, error);
      }
    }
  }, [activeUsers]);
  
  return {
    forceSyncAll,
    activeListeners: listenersRef.current.size
  };
};

// Utility function per debounce
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}